// Utilities
import { computed, nextTick, onScopeDispose, reactive, resolveDynamicComponent, toRef } from 'vue';
import { deepEqual, getCurrentInstance, hasEvent, IN_BROWSER, isString, propsFactory } from "../util/index.js"; // Types
export function useRoute() {
  const vm = getCurrentInstance('useRoute');
  return computed(() => vm?.proxy?.$route);
}
export function useRouter() {
  return getCurrentInstance('useRouter')?.proxy?.$router;
}
export function useLink(props, attrs) {
  const RouterLink = resolveDynamicComponent('RouterLink');
  const isLink = toRef(() => !!(props.href || props.to));
  const isClickable = computed(() => {
    return isLink?.value || hasEvent(attrs, 'click') || hasEvent(props, 'click');
  });
  if (isString(RouterLink) || !('useLink' in RouterLink)) {
    const href = toRef(() => props.href);
    return {
      isLink,
      isRouterLink: toRef(() => false),
      isClickable,
      href,
      linkProps: reactive({
        href
      }),
      route: toRef(() => undefined),
      navigate: toRef(() => undefined)
    };
  }

  // vue-router useLink `to` prop needs to be reactive and useLink will crash if undefined
  const routerLink = RouterLink.useLink({
    to: toRef(() => props.to || ''),
    replace: toRef(() => props.replace)
  });
  // Actual link needs to be undefined when to prop is not used
  const link = computed(() => props.to ? routerLink : undefined);
  const route = useRoute();
  const isActive = computed(() => {
    if (!link.value) return false;
    // router still resolving initial navigation, according to posva:
    // - START_LOCATION has an empty matched array and its name is undefined (unlike 404 Not Page Found)
    // - if the router is still resolving the initial boot, we bypass the active check and returns
    //   isExactActive ?? false to prevent the flashing overlay on page refresh
    if (route.value && route.value.matched.length === 0 && route.value.name == null) {
      return link.value.isExactActive?.value ?? false;
    }
    if (!props.exact) return link.value.isActive?.value ?? false;
    if (!route.value) return link.value.isExactActive?.value ?? false;
    return link.value.isExactActive?.value && deepEqual(link.value.route.value.query, route.value.query);
  });
  const href = computed(() => props.to ? link.value?.route.value.href : props.href);
  const isRouterLink = toRef(() => !!props.to);
  return {
    isLink,
    isRouterLink,
    isClickable,
    isActive,
    route: toRef(() => link.value?.route.value),
    navigate: toRef(() => link.value?.navigate),
    href,
    linkProps: reactive({
      href,
      'aria-current': toRef(() => isActive.value ? 'page' : undefined),
      'aria-disabled': toRef(() => props.disabled && isLink.value ? 'true' : undefined),
      tabindex: toRef(() => props.disabled && isLink.value ? '-1' : undefined)
    })
  };
}
export const makeRouterProps = propsFactory({
  href: String,
  replace: Boolean,
  to: [String, Object],
  exact: Boolean
}, 'router');
let inTransition = false;
export function useBackButton(router, cb) {
  let popped = false;
  let disposed = false;
  let removeBefore;
  let removeAfter;
  if (IN_BROWSER && router?.beforeEach) {
    nextTick(() => {
      if (disposed) return;
      window.addEventListener('popstate', onPopstate);
      removeBefore = router.beforeEach(() => {
        if (!inTransition) {
          inTransition = true;
          return new Promise(resolve => {
            setTimeout(() => resolve(popped ? cb() : undefined));
          });
        }
        return popped ? cb() : undefined;
      });
      removeAfter = router?.afterEach(() => {
        inTransition = false;
      });
    });
    onScopeDispose(() => {
      disposed = true;
      window.removeEventListener('popstate', onPopstate);
      removeBefore?.();
      removeAfter?.();
    });
  }
  function onPopstate(e) {
    if (e.state?.replaced) return;
    popped = true;
    setTimeout(() => popped = false);
  }
}
//# sourceMappingURL=router.js.map